import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X } from '@phosphor-icons/react';

const STORAGE_KEY = 'divispend.cookie-consent.v1';

/**
 * GDPR/ePrivacy cookie consent banner. We only set the essentials (auth tokens
 * already live in localStorage), so the banner is informational + dismissable.
 */
export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (!stored) setVisible(true);
    } catch (_) {
      // localStorage blocked — fail silently, don't show banner repeatedly
    }
  }, []);

  const accept = (value) => {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ value, at: new Date().toISOString() })
      );
    } catch (_) {}
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      data-testid="cookie-banner"
      className="fixed inset-x-0 bottom-0 z-[60] md:bottom-4 md:right-4 md:left-auto md:max-w-md"
    >
      <div className="m-3 md:m-0 bg-[#0A0A0A] border border-[#22C55E]/30 rounded-sm shadow-2xl shadow-black/60 p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-sm bg-[#22C55E]/15 border border-[#22C55E]/30 flex items-center justify-center shrink-0">
            <Cookie className="w-5 h-5 text-[#22C55E]" weight="duotone" />
          </div>
          <div className="flex-1 min-w-0">
            <div
              className="text-white font-bold text-sm mb-1.5"
              style={{ fontFamily: 'Cabinet Grotesk' }}
            >
              We use essential cookies only
            </div>
            <p className="text-[#999999] text-xs leading-relaxed mb-3">
              Divispend stores auth tokens in your browser&apos;s localStorage and uses{' '}
              <Link to="/trust" className="text-[#22C55E] hover:underline" data-testid="cookie-trust-link">
                SOC 2-aligned
              </Link>{' '}
              controls. No analytics or advertising cookies. See our{' '}
              <Link to="/privacy_policy" className="text-[#22C55E] hover:underline" data-testid="cookie-privacy-link">
                privacy policy
              </Link>
              .
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => accept('accept')}
                data-testid="cookie-accept-btn"
                className="bg-[#22C55E] hover:bg-[#16A34A] text-white text-xs font-bold rounded-full px-4 py-2 transition-colors"
              >
                Got it
              </button>
              <Link
                to="/privacy_policy"
                data-testid="cookie-learn-btn"
                className="text-[#999999] hover:text-white text-xs font-semibold rounded-full px-4 py-2 transition-colors flex items-center"
              >
                Learn more
              </Link>
            </div>
          </div>
          <button
            type="button"
            onClick={() => accept('dismiss')}
            data-testid="cookie-dismiss-btn"
            aria-label="Dismiss"
            className="text-[#666666] hover:text-white p-1 -mr-1 -mt-1"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
